import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { ScreenTitleBar } from "@/components/navigation/ScreenTitleBar";
import { Section } from "@/components/ui/section";
import { getChildLabel } from "@/features/selectedChild/getChildLabel";
import { TEST_IDS } from "@/testIdDefinition";

import { FilterChip } from "./FilterChip";
import { PeriodNavigator } from "./PeriodNavigator";
import { PeriodSwipeArea } from "./PeriodSwipeArea";
import { RequestList } from "./RequestList";
import { useSubmissionsScreen } from "./useSubmissionsScreen";

export function SubmissionsScreen() {
    const { t } = useTranslation();
    const {
        children,
        childrenById,
        showChildLabels,
        sections,
        dateWindow,
        windowShift,
        setWindowShift,
        childId,
        setChildId,
        isLoading,
        refreshControl,
    } = useSubmissionsScreen();

    const shiftPeriodBy = useCallback(
        (delta: number) => {
            setWindowShift((shift) => shift + delta);
        },
        [setWindowShift],
    );

    return (
        <SafeAreaView className="flex-1 bg-background" edges={["top"]}>
            <ScreenTitleBar title={t("requests.title")} className="mb-4" />
            <ScrollView
                className="flex-1"
                contentContainerClassName="grow gap-6 px-4 pb-6"
                refreshControl={refreshControl}
                testID={TEST_IDS[TEST_IDS.REQUESTS_SCREEN]}
            >
                {showChildLabels ? (
                    <View className="grow-0">
                        <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                        >
                            <View className="flex-row items-center gap-1">
                                <FilterChip
                                    label={t("requests.filterAllStudents")}
                                    selected={childId === null}
                                    onPress={() => setChildId(null)}
                                    testID={
                                        TEST_IDS[TEST_IDS.REQUESTS_FILTER_CHIP]
                                    }
                                />
                                {children.map((child) => (
                                    <FilterChip
                                        key={child.id}
                                        label={getChildLabel(child)}
                                        selected={childId === child.id}
                                        onPress={() => setChildId(child.id)}
                                        testID={
                                            TEST_IDS[
                                                TEST_IDS.REQUESTS_FILTER_CHIP
                                            ]
                                        }
                                    />
                                ))}
                            </View>
                        </ScrollView>
                    </View>
                ) : null}

                {isLoading ? (
                    <View className="items-center py-8">
                        <ActivityIndicator />
                    </View>
                ) : (
                    <View className="min-h-full flex-1">
                        <PeriodSwipeArea onShiftBy={shiftPeriodBy}>
                            <PeriodNavigator
                                dateWindow={dateWindow}
                                windowShift={windowShift}
                                onShift={setWindowShift}
                            />

                            {sections.actionNeeded.length > 0 ? (
                                <Section
                                    title={t("requests.actionNeededTitle")}
                                    testID={
                                        TEST_IDS[
                                            TEST_IDS
                                                .REQUESTS_SECTION_ACTION_NEEDED
                                        ]
                                    }
                                >
                                    <RequestList
                                        requests={sections.actionNeeded}
                                        childrenById={childrenById}
                                        showChildLabels={showChildLabels}
                                    />
                                </Section>
                            ) : null}

                            <Section
                                testID={
                                    TEST_IDS[TEST_IDS.REQUESTS_SECTION_PERIOD]
                                }
                            >
                                <RequestList
                                    requests={sections.period}
                                    childrenById={childrenById}
                                    showChildLabels={showChildLabels}
                                    emptyLabel={t("requests.emptyPeriod")}
                                />
                            </Section>
                        </PeriodSwipeArea>
                    </View>
                )}
            </ScrollView>
        </SafeAreaView>
    );
}
